import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ModuleDataMilestoneService } from '../../../services/module-data-milestone.service';
import { ToasterService } from '../../../services/core/toaster.service';
import { first } from 'rxjs/operators';
import { trigger, state, style, transition, animate } from '@angular/animations';
import { CommunicationServiceService } from '../../widgets/services/communication-service.service';
import { ActivityLogsCummunicationsService } from '../activity-log/activity-logs-cummunications.service';
import { MODULE_IDS } from '../../../constants/module-ids.constant';

interface MilestoneTemplate {
  id: number;
  name: string;
  template_number: string;
  module: {
    id: number;
    name: string;
  } | null;
  creator: {
    id: number;
    name: string;
  } | null;
  milestones: Milestone[];
  created_at: string;
  updated_at: string;
}

interface Milestone {
  id: number;
  name: string;
  milestone_template_id: number;
  tasks?: Task[];
}

interface Task {
  id: number;
  task_name: string;
}

interface SelectedMilestone {
  templateId: number;
  milestoneName: string;
}

@Component({
    selector: 'app-module-milestone-selector',
    templateUrl: './module-milestone-selector.component.html',
    styleUrls: ['./module-milestone-selector.component.scss'],
    animations: [
        trigger('expandCollapse', [
            transition(':enter', [
                style({ height: 0, opacity: 0 }),
                animate('300ms ease-out', style({ height: '*', opacity: 1 }))
            ]),
            transition(':leave', [
                animate('300ms ease-in', style({ height: 0, opacity: 0 }))
            ])
        ])
    ],
    standalone: false
})
export class ModuleMilestoneSelectorComponent implements OnInit {

  dataId: number;    // The record ID (e.g., bid ID)
  moduleId: number;  // The module_id from modules table
  moduleName: string;

  templates: MilestoneTemplate[] = [];
  selectedTemplates: MilestoneTemplate[] = [];  // Store selected templates
  selectedMilestones: Set<any> = new Set();
  selectedTasks: Set<any> = new Set();
  isLoading: boolean = false;
  expandedTemplates: { [key: string]: boolean } = {};
  expandedMilestones: { [key: string]: boolean } = {};
  searchQuery: string = '';

  // ──────────────────────────────────────────────────────────────────────────
  // Smart Suggestions — milestones whose own conditions currently match this
  // record and that aren't already attached. Bid & Job only (backend returns []
  // for any other module). Each "Add" attaches that milestone + all its tasks.
  // ──────────────────────────────────────────────────────────────────────────
  suggestionsExpanded: boolean = true;
  suggestionsLoading: boolean = false;
  addingSuggestionIds: Set<number> = new Set<number>();
  suggestions: Array<{
    id: number;
    name: string;
    templateName: string;
    matched: string[];
    tasks: number[];
  }> = [];

  /** Fetch condition-matched suggestions (no-op for non Bid/Job modules). */
  private loadSuggestions(): void {
    const id = Number(this.moduleId);
    if (id !== MODULE_IDS.BID && id !== MODULE_IDS.JOB) {
      return;
    }
    this.suggestionsLoading = true;
    this.moduleDataMilestoneService
      .getSmartSuggestions(this.moduleId, this.dataId)
      .pipe(first())
      .subscribe({
        next: (res: any) => {
          const data = res?.data || [];
          this.suggestions = data.map((s: any) => ({
            id: s.milestone_id,
            name: s.name,
            templateName: s.template_name,
            matched: s.matched || [],
            tasks: (s.tasks || []).map((t: any) => t.id),
          }));
          this.suggestionsLoading = false;
        },
        error: () => {
          this.suggestions = [];
          this.suggestionsLoading = false;
        },
      });
  }

  /** Collapse/expand the suggestions section. */
  toggleSuggestions(): void {
    this.suggestionsExpanded = !this.suggestionsExpanded;
  }

  /** Attach this one suggested milestone (with all its tasks); remove it on success. */
  addSuggestion(s: { id: number; tasks: number[] }): void {
    if (this.addingSuggestionIds.has(s.id)) {
      return;
    }
    this.addingSuggestionIds.add(s.id);
    this.moduleDataMilestoneService
      .attachMilestones({
        module_id: this.moduleId,
        data_id: this.dataId,
        milestones: [{ id: s.id, tasks: s.tasks || [] }],
      })
      .pipe(first())
      .subscribe({
        next: (response: any) => {
          this.toasterService.showSuccessMessage(response?.message || 'Milestone added');
          this.suggestions = this.suggestions.filter((x) => x.id !== s.id);
          this.addingSuggestionIds.delete(s.id);
          // Refresh the milestone/task views + activity log.
          this.communicationService.setMilestoneRefresh(true);
          this.activityLogService.setActivityService(true);
        },
        error: (error: any) => {
          this.addingSuggestionIds.delete(s.id);
          this.toasterService.showError(error?.error?.message || 'Failed to add milestone');
        },
      });
  }

  constructor(
    public dialogRef: MatDialogRef<ModuleMilestoneSelectorComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private moduleDataMilestoneService: ModuleDataMilestoneService,
    private toasterService: ToasterService,
    private communicationService: CommunicationServiceService,
    private activityLogService: ActivityLogsCummunicationsService
  ) {
    this.dataId = data.dataId;
    this.moduleId = data.moduleId;
    this.moduleName = data.moduleName || '';
  }

  ngOnInit(): void {
    // Show the module's templates by default as a browsable list; the search box
    // filters this same list. Selecting from it works the same as search+select.
    this.loadAvailableTemplates();
    // Condition-matched suggestions (Bid & Job only).
    this.loadSuggestions();
  }

  loadAvailableTemplates(search: string = ''): void {
    if (!this.moduleId) {
      this.toasterService.showError('Module ID is missing. Cannot load templates.');
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.moduleDataMilestoneService.getAvailableTemplates(this.moduleId, search)
      .pipe(first())
      .subscribe({
        next: (response) => {
          // The response should have { data: [...] }
          if (response && response.data) {
            this.templates = Array.isArray(response.data) ? response.data : [response.data];
          } else if (response && Array.isArray(response)) {
            this.templates = response;
          } else {
            this.templates = [];
          }
          // Initialize all templates and milestones as collapsed by default
          this.templates.forEach((template, index) => {
            this.expandedTemplates['template' + index] = false; // Collapsed by default
            // Initialize milestone expansion states as collapsed
            if (template.milestones) {
              template.milestones.forEach((milestone, mIndex) => {
                this.expandedMilestones['template' + index + '_milestone' + mIndex] = false; // Collapsed by default
              });
            }
          });
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading templates:', error);
          this.toasterService.showError('Failed to load milestone templates');
          this.templates = [];
          this.isLoading = false;
        }
      });
  }



  /**
   * Toggle selection of individual milestone with hierarchy
   */
  toggleMilestone(milestone: Milestone): void {
    if (this.selectedMilestones.has(milestone.id)) {
      // Unchecking a milestone
      this.selectedMilestones.delete(milestone.id);
      // Also unselect all tasks in this milestone
      if (milestone.tasks && milestone.tasks.length > 0) {
        milestone.tasks.forEach(task => {
          this.selectedTasks.delete(task.id);
        });
      }

      // Check if this milestone belongs to any selected template
      // If no milestones from a template are selected, remove the template
      this.selectedTemplates.forEach(template => {
        if (template.milestones) {
          const anyMilestoneSelected = template.milestones.some(m => this.selectedMilestones.has(m.id));
          if (!anyMilestoneSelected) {
            // Remove template from selected templates if no milestones are selected
            const index = this.selectedTemplates.findIndex(t => t.id === template.id);
            if (index > -1) {
              this.selectedTemplates.splice(index, 1);
            }
          }
        }
      });
    } else {
      // Checking a milestone
      this.selectedMilestones.add(milestone.id);
      // Also select all tasks in this milestone
      if (milestone.tasks && milestone.tasks.length > 0) {
        milestone.tasks.forEach(task => {
          this.selectedTasks.add(task.id);
        });
      }
    }
  }


  /**
   * Check if individual milestone is selected
   * A milestone shows as selected if it's in the selected set (regardless of task selection)
   */
  isMilestoneSelected(milestone: Milestone): boolean {
    return this.selectedMilestones.has(milestone.id);
  }

  /**
   * Save selected milestones with their tasks
   */
  save(): void {
    if (this.selectedMilestones.size === 0) {
      this.toasterService.showWarning('Please select at least one milestone');
      return;
    }

    this.isLoading = true;

    // Build hierarchical milestone-task structure
    const milestonesData: any[] = [];

    // Go through selected templates to get milestone-task relationships
    this.selectedTemplates.forEach(template => {
      if (template.milestones) {
        template.milestones.forEach(milestone => {
          // Check if this milestone is selected
          if (this.selectedMilestones.has(milestone.id)) {
            // Get selected tasks for this milestone
            const selectedTaskIds: number[] = [];
            if (milestone.tasks) {
              milestone.tasks.forEach(task => {
                if (this.selectedTasks.has(task.id)) {
                  selectedTaskIds.push(task.id);
                }
              });
            }

            // Add milestone with its selected tasks
            milestonesData.push({
              id: milestone.id,
              tasks: selectedTaskIds
            });
          }
        });
      }
    });

    // Remove duplicates (in case same milestone appears in multiple templates)
    const uniqueMilestones = milestonesData.reduce((acc: any[], current) => {
      const exists = acc.find(item => item.id === current.id);
      if (!exists) {
        acc.push(current);
      }
      return acc;
    }, []);

    const payload = {
      module_id: this.moduleId,
      data_id: this.dataId,
      milestones: uniqueMilestones
    };

    console.log('Sending payload:', payload); // For debugging

    this.moduleDataMilestoneService.attachMilestones(payload)
    .pipe(first())
    .subscribe({
      next: (response) => {
        this.toasterService.showSuccessMessage(response.message || 'Milestones attached successfully');
        // Trigger milestone refresh for all subscribers
        this.communicationService.setMilestoneRefresh(true);
        // Trigger activity log refresh
        this.activityLogService.setActivityService(true);
        this.dialogRef.close(response.data);
      },
      error: (error) => {
        this.isLoading = false;
        const errorMessage = error?.error?.message || 'Failed to attach milestones';
        this.toasterService.showError(errorMessage);
      }
    });
  }

  /**
   * Toggle template expansion
   */
  toggleTemplate(templateKey: string): void {
    this.expandedTemplates[templateKey] = !this.expandedTemplates[templateKey];
  }

  /**
   * Check if all milestones and tasks in a template are selected
   */
  isTemplateSelected(template: MilestoneTemplate): boolean {
    // Check if template is in selectedTemplates array
    return this.selectedTemplates.some(t => t.id === template.id);
  }

  /**
   * Check if some but not all milestones in a template are selected (indeterminate state)
   */
  isTemplateIndeterminate(template: MilestoneTemplate): boolean {
    if (!template.milestones || template.milestones.length === 0) {
      return false;
    }
    const selectedCount = template.milestones.filter(milestone => this.selectedMilestones.has(milestone.id)).length;
    return selectedCount > 0 && selectedCount < template.milestones.length;
  }

  /**
   * Toggle selection of all milestones and tasks in a template
   */
  toggleTemplateSelection(template: MilestoneTemplate, event: any): void {
    if (!template.milestones || template.milestones.length === 0) {
      return;
    }

    if (event.checked) {
      // Add template to selected templates if not already there
      const templateIndex = this.selectedTemplates.findIndex(t => t.id === template.id);
      if (templateIndex === -1) {
        this.selectedTemplates.push(template);
        // Initialize expansion state for this template
        const newIndex = this.selectedTemplates.length - 1;
        this.expandedTemplates['template' + newIndex] = false;
        if (template.milestones) {
          template.milestones.forEach((milestone, mIndex) => {
            this.expandedMilestones['template' + newIndex + '_milestone' + mIndex] = false;
          });
        }
      }
      // Select all milestones and their tasks in this template
      template.milestones.forEach(milestone => {
        this.selectedMilestones.add(milestone.id);
        // Also select all tasks in this milestone
        if (milestone.tasks && milestone.tasks.length > 0) {
          milestone.tasks.forEach(task => {
            this.selectedTasks.add(task.id);
          });
        }
      });
    } else {
      // Remove template from selected templates
      this.selectedTemplates = this.selectedTemplates.filter(t => t.id !== template.id);
      // Unselect all milestones and their tasks in this template
      template.milestones.forEach(milestone => {
        this.selectedMilestones.delete(milestone.id);
        // Also unselect all tasks in this milestone
        if (milestone.tasks && milestone.tasks.length > 0) {
          milestone.tasks.forEach(task => {
            this.selectedTasks.delete(task.id);
          });
        }
      });
    }
  }

  /**
   * Cancel and close dialog
   */
  cancel(): void {
    this.dialogRef.close();
  }

  /**
   * Handle search when Enter is pressed
   */
  onSearchEnter(): void {
    // Empty search restores the full listing; otherwise filter by the term.
    this.loadAvailableTemplates(this.searchQuery.trim());
  }

  /**
   * Handle search input changes — restore the full listing as soon as the box
   * is cleared (so deleting the search brings the list back without pressing Enter).
   */
  onSearchInput(): void {
    if (!this.searchQuery.trim()) {
      this.loadAvailableTemplates();
    }
  }

  /**
   * Clear search but keep selected items; reload the full listing.
   */
  clearSearch(): void {
    this.searchQuery = '';
    this.loadAvailableTemplates();
    // Don't clear selected items - we want to keep them
  }

  /**
   * Toggle milestone expansion state
   */
  toggleMilestoneExpansion(milestoneKey: string): void {
    this.expandedMilestones[milestoneKey] = !this.expandedMilestones[milestoneKey];
  }

  /**
   * Toggle task selection with hierarchy
   */
  toggleTask(task: Task, milestone: Milestone): void {
    if (this.selectedTasks.has(task.id)) {
      // Unchecking a task
      this.selectedTasks.delete(task.id);

      // Check if ALL tasks are now unchecked for this milestone
      if (milestone.tasks) {
        const anyTaskSelected = milestone.tasks.some(t => this.selectedTasks.has(t.id));
        if (!anyTaskSelected) {
          // Only uncheck milestone if NO tasks are selected
          this.selectedMilestones.delete(milestone.id);

          // Check if unchecking this milestone means all milestones in template are unchecked
          this.selectedTemplates.forEach(template => {
            if (template.milestones) {
              const anyMilestoneSelected = template.milestones.some(m => this.selectedMilestones.has(m.id));
              if (!anyMilestoneSelected) {
                // Remove template from selected templates if no milestones are selected
                const index = this.selectedTemplates.findIndex(t => t.id === template.id);
                if (index > -1) {
                  this.selectedTemplates.splice(index, 1);
                }
              }
            }
          });
        }
        // If at least one task is still selected, keep milestone selected
        // (no need to explicitly add, it's already in the set)
      }
    } else {
      // Checking a task
      this.selectedTasks.add(task.id);

      // Always ensure milestone is selected when a task is selected
      if (!this.selectedMilestones.has(milestone.id)) {
        this.selectedMilestones.add(milestone.id);
      }
    }
  }

  /**
   * Check if task is selected
   */
  isTaskSelected(task: Task): boolean {
    return this.selectedTasks.has(task.id);
  }

}